import { useState } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import type { ContextMenuItem } from 'cpool-components/general/ContextMenu';
import { ContextMenuItemVariant } from 'cpool-components/general/ContextMenu/types';
import {
    AGGREGATE_NAMES,
    CONSORS_DOWNLOAD_SUBSCRIPTION_DOCUMENT_REQUEST_STATUSES,
    LOAN_REQUEST_OFFER_SOURCES,
} from 'cpool-constants';
import type { SchemaLoanRequestResolver_GetAcceptedOffers } from 'cpool-tables/backend-resolvers';
import { useTranslation } from 'react-i18next';
import { BsChatDots } from 'react-icons/bs';
import { IoTrashOutline } from 'react-icons/io5';
import { TbCloudDownload, TbCloudUpload, TbSend } from 'react-icons/tb';
import { toast } from 'react-toastify';

import {
    downloadOfferDocuments,
    getConsorsDownloadFailureReason,
    getManualOfferDocument,
    openEuropaceDocumentsInNewTabs,
} from './utils/downloadOfferDocuments';
import { useCancelConsorsSubscription } from '../../../hooks/useCancelConsorsSubscription';
import { useOfferDocumentsUpload } from '../../../hooks/useOfferDocumentsUpload';
import { useGetConsorsNotesByLeadId } from '../../../Notes/hooks/useGetConsorsNotesByLeadId';
import { isEuropaceOffer } from '../../../types';
import { getIsActiveConsorsOffer } from '~/features/leadDetails/components/utils/getIsActiveConsorsOffer';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { useConsorsSubscriptionStatus } from '~/features/leadDetails/hooks/useConsorsSubscriptionStatus';
import useLead from '~/features/leadDetails/hooks/useLead';

type AcceptedOffer = SchemaLoanRequestResolver_GetAcceptedOffers[number];

type Params = {
    acceptedOffer: AcceptedOffer;
    onOpenConsorsNotes: () => void;
    onOpenOfferEmailDialog: (offerId: string) => void;
};

const ICON_SIZE = 16;

export const useOfferActions = ({ acceptedOffer, onOpenConsorsNotes, onOpenOfferEmailDialog }: Params) => {
    const { t } = useTranslation();
    const { useParams } = getRouteApi('/_app/leads_/details/$leadId');
    const { leadId } = useParams();
    const { data: lead } = useLead(leadId);
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { data: consorsNotes } = useGetConsorsNotesByLeadId(leadId);
    const { mutate: cancelOffer } = useCancelConsorsSubscription();
    const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);

    const { data: consorsSubscription } = useConsorsSubscriptionStatus({
        loanRequestId: acceptedOffer.loanRequestId,
        subscriptionIdentifier:
            acceptedOffer.source === LOAN_REQUEST_OFFER_SOURCES.CONSORS
                ? acceptedOffer.subscriptionIdentifier
                : undefined,
    });

    const { acknowledgedDocumentsQuery, uploadConfirmedDocuments } = useOfferDocumentsUpload({
        applicationId: acceptedOffer.applicationId,
        offerId: acceptedOffer.id,
        processId: lead?.europace?.id ?? null,
        source: acceptedOffer.source,
    });

    const isStatusChangeDenied = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].changeOfferStatus;
    const isActiveConsorsOffer = getIsActiveConsorsOffer(acceptedOffer);
    const isOfferFromEuropace = isEuropaceOffer(acceptedOffer);
    const hasNewConsorsNotes = Boolean(consorsNotes?.hasNewConsorsNotes) && isActiveConsorsOffer;

    const consorsOfferDocumentCreated =
        acceptedOffer.source === LOAN_REQUEST_OFFER_SOURCES.CONSORS &&
        consorsSubscription?.documentDownload?.status ===
            CONSORS_DOWNLOAD_SUBSCRIPTION_DOCUMENT_REQUEST_STATUSES.SUCCESS;

    const isDownloadDisabled =
        (isOfferFromEuropace && acceptedOffer.documents?.length === 0) ||
        (acceptedOffer.source === LOAN_REQUEST_OFFER_SOURCES.MANUAL && !getManualOfferDocument(acceptedOffer)) ||
        (acceptedOffer.source === LOAN_REQUEST_OFFER_SOURCES.CONSORS && !consorsOfferDocumentCreated);

    const download = async () => {
        try {
            await downloadOfferDocuments(acceptedOffer);
        } catch (error) {
            openEuropaceDocumentsInNewTabs(acceptedOffer);

            const consorsFailureReason = getConsorsDownloadFailureReason(acceptedOffer, error);
            if (
                consorsFailureReason &&
                t(`OFFER_MANAGEMENT.TOASTS.${consorsFailureReason}`) !==
                    `OFFER_MANAGEMENT.TOASTS.${consorsFailureReason}`
            ) {
                toast.error(t(`OFFER_MANAGEMENT.TOASTS.${consorsFailureReason}`));
                return;
            }

            toast.error(
                <div>
                    <p>{t('OFFER_MANAGEMENT.TOASTS.DOWNLOAD_FAILED')}</p>
                    {error instanceof Error && error.message && <p>{error.message}</p>}
                </div>,
            );
        }
    };

    const items: ContextMenuItem[] = [
        {
            disabled: isStatusChangeDenied,
            icon: <TbSend size={ICON_SIZE} />,
            id: 'send-offer',
            label: t('OFFER_MANAGEMENT.ICONS_TOOLTIP.SEND_OFFER'),
            onClick: () => onOpenOfferEmailDialog(acceptedOffer.id),
        },
        {
            disabled: isDownloadDisabled,
            icon: <TbCloudDownload size={ICON_SIZE} />,
            id: 'download-offer',
            label: t('OFFER_MANAGEMENT.ICONS_TOOLTIP.DOWNLOAD'),
            onClick: download,
        },
        {
            disabled: !isOfferFromEuropace || isStatusChangeDenied,
            icon: <TbCloudUpload size={ICON_SIZE} />,
            id: 'transfer-documents',
            label: t('OFFER_MANAGEMENT.ICONS_TOOLTIP.TRANSFER_TO_BANK'),
            onClick: () => setIsTransferDialogOpen(true),
        },
        {
            disabled: !isActiveConsorsOffer || isStatusChangeDenied,
            icon: <BsChatDots className={hasNewConsorsNotes ? 'text-alert-error' : undefined} size={ICON_SIZE} />,
            id: 'bank-chat',
            label: t('OFFER_MANAGEMENT.ICONS_TOOLTIP.BANK_CHAT'),
            onClick: onOpenConsorsNotes,
        },
        {
            disabled:
                !isActiveConsorsOffer ||
                isStatusChangeDenied ||
                !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].cancelConsorsSubscription,
            icon: <IoTrashOutline size={ICON_SIZE} />,
            id: 'cancel-offer',
            label: t('OFFER_MANAGEMENT.ICONS_TOOLTIP.CANCEL_OFFER'),
            onClick: () => cancelOffer(),
            variant: ContextMenuItemVariant.DESTRUCTIVE,
        },
    ];

    // Mirrors the filter inside uploadConfirmedDocuments so the dialog lists exactly what gets transferred
    const europacePlatform = lead?.europace?.platform;
    const transferableDocuments = (acknowledgedDocumentsQuery.data ?? []).filter(
        (document) => europacePlatform && !document.uploadedTo?.europacePlatform?.[europacePlatform],
    );

    return {
        acknowledgedDocuments: transferableDocuments,
        closeTransferDialog: () => setIsTransferDialogOpen(false),
        hasNewConsorsNotes,
        isTransferDialogOpen,
        items,
        transferDocumentsToBank: uploadConfirmedDocuments,
    };
};
